import React, { useState } from "react";
import { Heart, Flag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * School gallery.
 * Preferred usage: pass `photos=[{photo_id, data_url, caption, like_count, liked_by_me, report_count}]` and `onReload`.
 * Legacy: pass `mainUrl` and `images` (string URLs).
 */
export default function Gallery({ mainUrl, images = [], alt = "", photos = null, onReload }) {
  const { user } = useAuth();
  const [active, setActive] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("inappropriate");
  const [reportDetail, setReportDetail] = useState("");
  const [busy, setBusy] = useState(false);

  const rich = Array.isArray(photos) && photos.length > 0;
  const items = rich
    ? photos.map((p) => ({ url: p.data_url, meta: p }))
    : [mainUrl, ...(images || [])].filter(Boolean).map((u) => ({ url: u, meta: null }));

  if (!items.length) return null;
  const current = items[active] || items[0];

  const toggleLike = async () => {
    if (!user) { toast.error("Masuk untuk menyukai foto"); return; }
    if (!current.meta) return;
    try {
      setBusy(true);
      await api.post(`/photos/${current.meta.photo_id}/like`);
      onReload && (await onReload());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const submitReport = async () => {
    if (!user) { toast.error("Masuk untuk melaporkan foto"); return; }
    if (!current.meta) return;
    try {
      setBusy(true);
      const res = await api.post(`/photos/${current.meta.photo_id}/report`, { reason: reportReason, detail: reportDetail });
      if (res.data?.already) toast.info("Anda sudah melaporkan foto ini");
      else toast.success("Laporan terkirim ke moderator");
      setReportOpen(false);
      setReportDetail("");
      onReload && (await onReload());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim laporan");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="school-gallery" className="space-y-3">
      <div className="rounded-2xl overflow-hidden border border-[#EAE7E0] bg-[#F3F1ED] relative">
        <img
          src={current.url}
          alt={alt}
          className="w-full max-h-[440px] object-cover"
          data-testid={`gallery-main-${active}`}
        />
        {current.meta && (
          <div className="absolute top-3 right-3 flex items-center gap-2" data-testid="gallery-photo-actions">
            <button
              type="button"
              disabled={busy}
              onClick={toggleLike}
              data-testid="gallery-like-btn"
              className={
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs backdrop-blur transition-colors " +
                (current.meta.liked_by_me
                  ? "bg-[#DC2626]/95 border-[#DC2626] text-white"
                  : "bg-white/90 border-[#EAE7E0] text-[#1F2937] hover:bg-white")
              }
              title={current.meta.liked_by_me ? "Batalkan suka" : "Sukai foto"}
            >
              <Heart size={14} className={current.meta.liked_by_me ? "fill-white" : ""} />
              <span data-testid="gallery-like-count">{current.meta.like_count || 0}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (!user) { toast.error("Masuk untuk melaporkan foto"); return; }
                setReportOpen(true);
              }}
              data-testid="gallery-report-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAE7E0] bg-white/90 hover:bg-white text-xs text-[#4B5563] backdrop-blur"
              title="Laporkan foto tidak pantas"
            >
              <Flag size={14} /> <span className="hidden sm:inline">Laporkan</span>
            </button>
          </div>
        )}
      </div>

      {current.meta?.caption && (
        <div className="text-sm text-[#4B5563] italic px-1" data-testid="gallery-caption">
          "{current.meta.caption}"
          {current.meta.uploader_name && <span className="not-italic text-[#4B5563]"> — {current.meta.uploader_name}</span>}
        </div>
      )}

      {items.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {items.map((it, i) => (
            <button
              key={(it.url || "") + i}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`gallery-thumb-${i}`}
              className={
                "aspect-[4/3] rounded-xl overflow-hidden border transition-all duration-200 " +
                (i === active
                  ? "border-[#1E3A8A] ring-2 ring-[#1E3A8A]/30"
                  : "border-[#EAE7E0] opacity-80 hover:opacity-100")
              }
            >
              <img src={it.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="photo-report-dialog">
          <DialogHeader>
            <DialogTitle>Laporkan foto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1.5">Alasan</div>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                data-testid="photo-report-reason"
                className="w-full h-10 border border-[#EAE7E0] rounded-lg px-3 bg-white text-sm"
              >
                <option value="inappropriate">Tidak pantas / menyinggung</option>
                <option value="privacy">Melanggar privasi anak</option>
                <option value="not_related">Bukan foto sekolah</option>
                <option value="misleading">Menyesatkan / hoaks</option>
                <option value="other">Lainnya</option>
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-1.5">Detail (opsional)</div>
              <Textarea
                value={reportDetail}
                onChange={(e) => setReportDetail(e.target.value)}
                data-testid="photo-report-detail"
                rows={3}
                maxLength={400}
                placeholder="Jelaskan singkat…"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReportOpen(false)} className="rounded-full h-10">Batal</Button>
              <Button disabled={busy} onClick={submitReport} data-testid="photo-report-submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">
                {busy ? "Mengirim…" : "Kirim laporan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
